/* =========================================================================
   tweaks.tsx — Tweaks panel: accent color, density, chart style.
   ========================================================================= */

import { useEffect } from "react";
import {
  useTweaks,
  TweaksPanel,
  TweakSection,
  TweakColor,
  TweakRadio,
  TweakToggle,
} from "./tweaks-panel";

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/ {
  accent: "orange",
  density: "comfortable",
  chartStyle: "cinematic",
  starfield: false,
  showAuditTrail: true,
}; /*EDITMODE-END*/

function ApplyTweaks({ tweaks }: { tweaks: Record<string, unknown> }) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("accent-azure", "accent-emerald", "accent-violet");
    if (tweaks.accent === "azure") root.classList.add("accent-azure");
    if (tweaks.accent === "emerald") root.classList.add("accent-emerald");
    if (tweaks.accent === "violet") root.classList.add("accent-violet");

    document.body.classList.toggle(
      "density-compact",
      tweaks.density === "compact",
    );
    (globalThis as unknown as Record<string, unknown>).__chartStyle =
      tweaks.chartStyle;
    (globalThis as unknown as Record<string, unknown>).__starfield =
      tweaks.starfield;
  }, [tweaks]);
  return null;
}

export function CampusTweaks() {
  const [tweaks, setTweak] = useTweaks(TWEAK_DEFAULTS);

  return (
    <>
      <ApplyTweaks tweaks={tweaks} />
      <TweaksPanel title="Tweaks" noDeckControls={false}>
        <TweakSection label="Accent color">
          <TweakColor
            value={
              tweaks.accent === "orange"
                ? "#F08A28"
                : tweaks.accent === "azure"
                  ? "#3B83F0"
                  : tweaks.accent === "emerald"
                    ? "#1FB874"
                    : "#9C5AE6"
            }
            onChange={(c) => {
              const map: Record<string, string> = {
                "#F08A28": "orange",
                "#3B83F0": "azure",
                "#1FB874": "emerald",
                "#9C5AE6": "violet",
              };
              const key = typeof c === "string" ? c : String(c);
              setTweak("accent", map[key] ?? "orange");
            }}
            options={["#F08A28", "#3B83F0", "#1FB874", "#9C5AE6"]}
          />
        </TweakSection>

        <TweakSection label="Layout density">
          <TweakRadio
            value={String(tweaks.density)}
            onChange={(v) => setTweak("density", v)}
            options={[
              { value: "comfortable", label: "Comfortable" },
              { value: "compact", label: "Compact" },
            ]}
          />
        </TweakSection>

        <TweakSection label="Chart style">
          <TweakRadio
            value={String(tweaks.chartStyle)}
            onChange={(v) => setTweak("chartStyle", v)}
            options={[
              { value: "cinematic", label: "Cinematic" },
              { value: "flat", label: "Flat" },
            ]}
          />
        </TweakSection>

        <TweakSection label="Hero starfield">
          <TweakToggle
            value={!!tweaks.starfield}
            onChange={(v) => setTweak("starfield", v)}
            label={tweaks.starfield ? "Stars on" : "Stars off"}
          />
        </TweakSection>
      </TweaksPanel>
    </>
  );
}
